import { Component, OnInit, Input, OnChanges, OnDestroy } from '@angular/core';
import type { EChartsOption } from 'echarts';
import { SharedConstant } from '../../constants/sharedConstants';
import { Subscription } from 'rxjs';
import { SwitchThemeService } from '../../services/switch-theme/switch-theme.service';

@Component({
  selector: 'ems-donut-chart',
  templateUrl: './donut-chart.component.html',
  styleUrls: ['./donut-chart.component.scss']
})
export class DonutChartComponent implements OnInit, OnChanges, OnDestroy {
  @Input() labelVal: any;
  @Input() arrayVal: any;
  @Input() restrictBackgroundColorCode: any;
  @Input() chartTitle: any;
  chartOptions: EChartsOption = {};
  getSharedConstant = SharedConstant;
  themeSubscription!: Subscription;

  constructor(private switchThemeService: SwitchThemeService) { }
 
  ngOnInit() {
    this.loadChart();
    this.themeSubscription = this.switchThemeService.getCurrentTheme().subscribe(() => {
      this.loadChart();
    });
  }
  
  ngOnDestroy() {
    this.themeSubscription?.unsubscribe();
  }
  
  ngOnChanges() {
    this.loadChart();
  }
 
  private getThemeTextColor(): string {
    return getComputedStyle(document.documentElement).getPropertyValue('--text-primary').trim() || '#212529';
  }

  loadChart() {
    if (!this.arrayVal || !this.labelVal) { return; }
    const textColor = this.getThemeTextColor();

    // Build pie slice data by pairing each label with its value and color at the same index
    const pieData = this.labelVal.map((label: string, i: number) => ({
      value: this.arrayVal[i],
      name: label,
      itemStyle: { color: this.restrictBackgroundColorCode[i] }
    }));

    this.chartOptions = {
      // Tooltip shown on hover over a slice
      // {b} = slice name (e.g. "Today"), {c} = slice value (e.g. 42)
      tooltip: {
        trigger: 'item',
        formatter: '{b}: {c}'
      },

      // Center "Days" text inside the donut hole — only shown when chartTitle === 2
      title: this.chartTitle === 2 ? {
        text: this.getSharedConstant.doughnutChartLabels.daysTxt,
        left: 'center',
        top: '36%',       // slightly above vertical center to align with donut hole
        textStyle: {
          fontSize: 14,
          fontWeight: 'bold',
          color: textColor, // theme-aware: updates on light/dark mode switch
          align: 'center'
        }
      } : {},

      // Legend displayed horizontally at the bottom of the chart
      legend: {
        orient: 'horizontal',
        bottom: 0,
        left: 'center',
        itemWidth: 14,
        textStyle: { color: textColor, fontSize: 12 },
        data: [...this.labelVal].reverse()
      },
      aria: {
        enabled: true,
        decal: { show: false }
      },

      series: [
        {
          type: 'pie',
          radius: ['40%', '72%'], // inner radius and outer radius — creates the donut shape
          center: ['50%', '42%'], // horizontally centered, shifted up slightly to leave room for legend
          data: pieData,
          label: { show: false },           // hide labels on the slices themselves
          emphasis: { label: { show: false } } // also hide on hover
        }
      ]
    };
  }
}